import { ChangeEvent, useState } from "react";
import { CheckmarkInput, FileEditAltIcon, RichText, TextInput } from "@datagame/ui";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { GAME_ALL } from "../../../../../../../../../graphql/queries/GameQueries";
import { Section } from "../../../../Common";
import { TooltipWithIcon } from "../../../../../../../../shared/ui/TooltipWithIcon";
import { TRIVIA_ANSWER_OPTION_UPDATE } from "../../../../../../../../../graphql/mutations/TriviaAnswerOptionMutation";
import { TriviaAnswerOption, TriviaAnswerOptionUpdateInput } from "../../../../../../../../../gql/graphql";
import { useProject } from "../../../../../../../../../contexts/project/ProjectContext";

import styles from "./TriviaAnswerOptionInput.module.scss";

const cx = classNames.bind(styles);

export interface TriviaAnswerOptionInputProps {
  option: TriviaAnswerOption;
  currentActiveId?: string;
  questionId: string;
  onCurrentOptionChange: (id: string) => void;
}

export const TriviaAnswerOptionInput = ({
  option,
  onCurrentOptionChange,
  currentActiveId,
}: TriviaAnswerOptionInputProps) => {
  const { t: translation } = useTranslation("trivia");
  const t = (key: string) => translation(`advancedSettings.answers.${key}`) as string;
  const { project: { id: projectId } = {} } = useProject();
  const [text, setText] = useState(option.text);
  const [points, setPoints] = useState(option.points);
  const [explanation, setExplanation] = useState(option.explanation);
  const isCorrect = currentActiveId === option.id;

  const [updateTriviaAnswerOption] = useMutation(TRIVIA_ANSWER_OPTION_UPDATE);

  const saveOptionChanges = async (changes: Partial<TriviaAnswerOptionUpdateInput>, refetch = true, id = option.id) => {
    return updateTriviaAnswerOption({
      variables: {
        triviaAnswerOptionId: id,
        data: changes,
      },
      refetchQueries: refetch ? [{ query: GAME_ALL, variables: { projectId } }] : undefined,
    });
  };

  const handleOptionChange = async (e: ChangeEvent<HTMLInputElement>) => {
    onCurrentOptionChange(e.target.name);

    if (currentActiveId) {
      await saveOptionChanges({ isCorrect: false }, false, currentActiveId);
    }

    await saveOptionChanges({ isCorrect: true });
  };

  const handleTextChange = async () => saveOptionChanges({ text });

  const handlePointsChange = async () => {
    let newPoints = points;

    if (points < 0) {
      newPoints = Math.max(points, 0);
      setPoints(newPoints);
    }

    await saveOptionChanges({ points: newPoints });
  };

  const handleExplanationChange = async () => saveOptionChanges({ explanation });

  return (
    <div className={cx("answerOption", { correct: isCorrect })}>
      <Section
        className={cx("section", "textSection")}
        headerClassName={styles.sectionHeader}
        title={t("text.title")}
        tooltip={<TooltipWithIcon content={t("text.tooltip")} />}
      >
        <TextInput
          className={styles.textInput}
          icon={<FileEditAltIcon />}
          onBlur={handleTextChange}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setText(e.target.value)}
          size="small"
          value={text}
        />
      </Section>

      {isCorrect && (
        <Section
          className={cx("section", "pointsSection")}
          headerClassName={styles.sectionHeader}
          title={t("points.title")}
          tooltip={<TooltipWithIcon content={t("points.tooltip")} />}
        >
          <TextInput
            className={styles.pointsInput}
            min={0}
            size="small"
            onBlur={handlePointsChange}
            onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => setPoints(parseInt(value))}
            type="number"
            value={points}
          />
        </Section>
      )}

      <Section
        className={cx("section", "explanationSection")}
        headerClassName={styles.sectionHeader}
        title={t("explanation.title")}
        tooltip={<TooltipWithIcon content={t("explanation.tooltip")} />}
      >
        <RichText
          icon={<FileEditAltIcon />}
          value={explanation}
          onChange={(value) => setExplanation(value)}
          onBlur={handleExplanationChange}
          size="small"
          className={styles.input}
        />
      </Section>

      <Section
        className={cx("section", "correctnessSection")}
        headerClassName={styles.sectionHeader}
        title={t("correctness.title")}
        tooltip={<TooltipWithIcon content={t("correctness.tooltip")} />}
      >
        <div className={styles.checkmarkWrapper}>
          <CheckmarkInput
            className={styles.checkmarkInput}
            id={option.id}
            name={option.id}
            onChange={handleOptionChange}
            value={option.id}
            checked={option.id === currentActiveId}
          />
        </div>
      </Section>
    </div>
  );
};
